import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, ElementRef, Directive, Inject, Component, ViewEncapsulation, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { Subject, merge } from 'rxjs';
import { filter, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/cdk/platform';
import * as i2 from '@angular/cdk/scrolling';
import { trigger, transition, style, animate } from '@angular/animations';

class NgxNavbarDynamicExpandDirective {
    constructor(ngZone, platform, _document, viewportRuler, cdRef) {
        this.ngZone = ngZone;
        this.platform = platform;
        this._document = _document;
        this.onDestroy$ = new Subject();
        this.update$ = new Subject();
        this.nativeElement = inject(ElementRef).nativeElement;
        this.loaded = false;
        this._isExpanded = false;
        ngZone.runOutsideAngular(() => {
            merge(viewportRuler.change(150), this.update$)
                .pipe(filter(() => this.loaded ||
                (this.loaded = checkBootstrapStylesAreLoaded(_document, this.nativeElement))), map(() => {
                const element = this.nativeElement;
                let overflowSize;
                if (this.isExpanded) {
                    overflowSize = element.scrollWidth - element.offsetWidth;
                }
                else {
                    const clone = element.cloneNode(true);
                    clone.classList.add('navbar-expand');
                    const parent = element.parentElement;
                    if (parent) {
                        parent.appendChild(clone);
                        overflowSize = clone.scrollWidth - clone.offsetWidth;
                        parent.removeChild(clone);
                    }
                }
                return !overflowSize;
            }), distinctUntilChanged(), takeUntil(this.onDestroy$))
                .subscribe((isExpanded) => {
                ngZone.run(() => {
                    this._isExpanded = isExpanded;
                    cdRef.markForCheck();
                });
            });
        });
    }
    get isExpanded() {
        return this._isExpanded;
    }
    ngAfterContentInit() {
        if (!this.platform.isBrowser) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            this.loaded = checkBootstrapStylesAreLoaded(this._document, this.nativeElement);
            if (this.loaded) {
                this.update$.next();
            }
            else {
                setTimeout(() => {
                    this.ngAfterContentInit();
                }, 1000 / 60);
            }
        });
    }
    ngOnDestroy() {
        this.update$.complete();
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
NgxNavbarDynamicExpandDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarDynamicExpandDirective, deps: [{ token: i0.NgZone }, { token: i1.Platform }, { token: DOCUMENT }, { token: i2.ViewportRuler }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
NgxNavbarDynamicExpandDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.6", type: NgxNavbarDynamicExpandDirective, selector: "[ngxNavbarDynamicExpand]", host: { properties: { "class.navbar-expand": "isExpanded" }, classAttribute: "navbar text-nowrap" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarDynamicExpandDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxNavbarDynamicExpand]',
                    host: {
                        class: 'navbar text-nowrap',
                        '[class.navbar-expand]': 'isExpanded',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i1.Platform }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i2.ViewportRuler }, { type: i0.ChangeDetectorRef }]; } });
function checkBootstrapStylesAreLoaded(document, element) {
    const documentWindow = document.defaultView || window;
    const computedStyle = documentWindow && documentWindow.getComputedStyle
        ? documentWindow.getComputedStyle(element)
        : false;
    return computedStyle && computedStyle.whiteSpace === 'nowrap';
}

/** Time and timing curve for navbar animations. */
const NAVBAR_ANIMATION_TIMING = '300ms ease-out';
const ngxNavbarAnimations = {
    navbarSlide: trigger('slideState', [
        transition('false => true', [
            style({
                opacity: 0,
                height: 0,
                overflow: 'hidden'
            }),
            animate(NAVBAR_ANIMATION_TIMING, style({
                opacity: 1,
                height: '*'
            }))
        ]),
        transition('true => false', [
            animate(NAVBAR_ANIMATION_TIMING, style({
                display: 'block',
                opacity: 0,
                height: 0,
                overflow: 'hidden'
            }))
        ])
    ])
};

class NgxNavbarCollapseComponent {
    constructor() {
        this.isCollapsed = true;
        this.isTransitioning = false;
        this.nativeElement = inject(ElementRef).nativeElement;
        this.updateClasses();
    }
    toggle() {
        this.isTransitioning = true;
        this.isCollapsed = !this.isCollapsed;
        this.updateClasses();
    }
    close() {
        if (this.isCollapsed) {
            return;
        }
        this.isTransitioning = true;
        this.isCollapsed = true;
        this.updateClasses();
    }
    open() {
        if (!this.isCollapsed) {
            return;
        }
        this.isTransitioning = true;
        this.isCollapsed = false;
        this.updateClasses();
    }
    done() {
        this.isTransitioning = false;
        this.updateClasses();
    }
    updateClasses() {
        const classList = this.nativeElement.classList;
        const isTransitioning = this.isTransitioning;
        classList.toggle('collapse', !isTransitioning);
        classList.toggle('show', !this.isCollapsed && !isTransitioning);
        classList.toggle('collapsing', isTransitioning);
    }
}
NgxNavbarCollapseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarCollapseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
NgxNavbarCollapseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.0.6", type: NgxNavbarCollapseComponent, selector: "ngx-navbar-collapse", host: { listeners: { "@slideState.done": "done()" }, properties: { "@slideState": "!isCollapsed" }, classAttribute: "navbar-collapse ngx-navbar-collapse" }, exportAs: ["ngxNavbarCollapse"], ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true, styles: [".ngx-navbar-collapse.collapsing{height:auto}\n"], animations: [ngxNavbarAnimations.navbarSlide], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarCollapseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-navbar-collapse', template: ` <ng-content></ng-content> `, host: {
                        class: 'navbar-collapse ngx-navbar-collapse',
                        '[@slideState]': '!isCollapsed',
                        '(@slideState.done)': 'done()',
                    }, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, animations: [ngxNavbarAnimations.navbarSlide], exportAs: 'ngxNavbarCollapse', styles: [".ngx-navbar-collapse.collapsing{height:auto}\n"] }]
        }], ctorParameters: function () { return []; } });

class NgxNavbarModule {
}
NgxNavbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxNavbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarModule, declarations: [NgxNavbarCollapseComponent, NgxNavbarDynamicExpandDirective], imports: [CommonModule], exports: [NgxNavbarCollapseComponent, NgxNavbarDynamicExpandDirective] });
NgxNavbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: NgxNavbarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NgxNavbarCollapseComponent, NgxNavbarDynamicExpandDirective],
                    imports: [CommonModule],
                    exports: [NgxNavbarCollapseComponent, NgxNavbarDynamicExpandDirective]
                }]
        }] });

/*
 * Public API Surface of ngx-bootstrap-navbar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NAVBAR_ANIMATION_TIMING, NgxNavbarCollapseComponent, NgxNavbarDynamicExpandDirective, NgxNavbarModule, ngxNavbarAnimations };
